import { ROLES, type Role } from '../../shared/contract';
import { cx } from '../lib/cx';
import styles from './RoleToggle.module.css';

/**
 * Label for each role, kept next to the component that renders it.
 *
 * The enum values (`creator`/`buyer`) and their EmailOctopus tags are legacy —
 * changing them would orphan the tags already on the live list. The labels are
 * what the visitor reads, so they carry the split that matters now: someone who
 * wants to be found on the board vs. someone who's here to discover it.
 */
const ROLE_LABELS: Record<Role, string> = {
  creator: 'I want to be found',
  buyer: "I'm here to discover",
};

interface RoleToggleProps {
  value: Role;
  onChange: (role: Role) => void;
  disabled?: boolean;
}

/**
 * Segmented control for the "rank vs. browse" split.
 *
 * Rendered as buttons with `aria-pressed` rather than radios: it reads as a
 * toggle to assistive tech, which matches how it looks and behaves.
 */
export function RoleToggle({ value, onChange, disabled = false }: RoleToggleProps) {
  return (
    <div className={styles.toggle} role="group" aria-label="I am a">
      {ROLES.map((role) => (
        <button
          key={role}
          type="button"
          className={cx(styles.option, role === value && styles.active)}
          aria-pressed={role === value}
          disabled={disabled}
          onClick={() => {
            onChange(role);
          }}
        >
          {ROLE_LABELS[role]}
        </button>
      ))}
    </div>
  );
}
